import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Col, DatePicker, Form, Input, Modal, Row, Select, Tooltip } from "antd";
import moment from "moment";
import {
  toHttpFormatStartOfDay,
  toHttpFormatEndOfDay,
  toHttpFormat,
  toDisplayFormat,
  displayFormat,
  infinityDateHttpFormat,
} from "../../lib/utils/formatDate";
import tickIcon from "../../img/done.svg";
import deleteIcon from "../../img/delete-round.svg";
import pencilWithPlusIcon from "../../img/pencil-plus.svg";
import pencilWithCalendarIcon from "../../img/pencil-calendar.svg";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import { ReactComponent as PlusInRound } from "../../img/plus-in-round.svg";
import { TableColumnType, TableRecord } from "../../lib/interfaces/TableRecord";
import { DictionaryTypeAlias } from "../../lib/types/DictionaryTypeAlias";
import { DictionariesSearchParams } from "../../lib/types/DictionariesSearchParams";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { selectFilterOption } from "../../lib/utils/selectFilterOption";
import { getSelectOptions } from "../../lib/utils/getSelectOptions";
import { useAppDispatch, useAppSelector } from "../../index";
import { TerminCreateModal } from "./TerminCreateModal/TerminCreateModal";
import { hasAccess } from "../RolesModule/hasAccess";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import { checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import {
  deleteTerminVersion,
  fetchDictionary,
  fetchMaxTeminId,
  fetchParentDictionary,
  fetchTerminVersions,
  updateTerminVersion,
} from "../../store/slices/dicts";
import "./Dictionaries.less";

const { TextArea } = Input;
const nowDate = toHttpFormat(moment());

export const Dictionaries = () => {
  const dispatch = useAppDispatch();

  const {
    dictionaryTypes,
    activeDictionaryLoading,
    activeDictionary,
    parentDictionary,
    terminVersionsLoading,
    terminVersions,
  } = useAppSelector((state) => state.dicts);

  const [form] = Form.useForm();

  const [selectedDicTypeAlias, setSelectedDicTypeAlias] = useState<DictionaryTypeAlias>();
  const [
    dictionariesSearchParams,
    setDictionariesSearchParams,
  ] = useState<DictionariesSearchParams>({
    includeClosed: false,
    fromDate: "",
  });

  const parentDicTypeAlias: DictionaryTypeAlias | null = useMemo(
    () =>
      dictionaryTypes?.find(({ alias }) => alias === selectedDicTypeAlias)?.parentDicTypeAlias ||
      null,
    [selectedDicTypeAlias, dictionaryTypes]
  );

  // Загрузка родительского словаря
  useEffect(() => {
    if (parentDicTypeAlias) {
      dispatch(fetchParentDictionary({ dicTypeAlias: parentDicTypeAlias }));
    }
  }, [parentDicTypeAlias]);

  const [isSearchDone, setIsSearchDone] = useState(false);
  const [tableData, setTableData] = useState<TableRecord[]>([]);
  const [dataVersion, setDataVersion] = useState<TableRecord[]>([]);
  const [objectId, setObjectId] = useState<number | null>(null);
  const [objectDateFrom, setObjectDateFrom] = useState("");
  const [objectDateTo, setObjectDateTo] = useState("");
  const [activeModal, setActiveModal] = useState<"createTermin" | "history" | "">("");

  const defalultDicRequestBody = {
    id: 0,
    fromDate: "",
    toDate: "",
    dicTypeAlias: "",
    name: "",
    abbrev: "",
    parentDicId: 0,
    dsc: "",
    alias: "",
  };

  const [dicRequestBody, setDicRequestBody] = useState(defalultDicRequestBody);

  // console.log(dicRequestBody);

  const [editingKey, setEditingKey] = useState<TableRecord["key"]>();
  const [dicEditStatus, setDicEditStatus] = useState("idle");
  const isEditing = (rec: TableRecord) => rec.key === editingKey;
  const isEditingDicToDate = dicEditStatus === "editingDicToDate";
  const isEditingDic = dicEditStatus === "editingDic";
  const isMakingNewDicVersion = dicEditStatus === "createNewVersion";

  const [selectedDicId, setSelectedDicId] = useState<number>();
  const [selectedParentDicId, setSelectedParentDicId] = useState<number>();

  const resetTableFilters = () => {
    setSelectedDicId(undefined);
    setSelectedParentDicId(undefined);
  };

  // Данные таблицы
  useEffect(() => {
    if (activeDictionary && isSearchDone) {
      setTableData(
        activeDictionary
          .map((dicTermin) => ({
            ...dicTermin,
            key: String(dicTermin.id),
            isActive: moment(dicTermin.toDate).isAfter(moment().startOf("day")),
            period: dicTermin,
            parentDicName:
              versionsTableUtils.getDictionaryItemById(parentDictionary, dicTermin.parentDicId)
                ?.name || "",
          }))
          .filter(({ id }) => (selectedDicId ? selectedDicId === id : true))
          .filter(({ parentDicId }) =>
            selectedParentDicId ? selectedParentDicId === parentDicId : true
          )
      );
    } else {
      setTableData([]);
    }
  }, [activeDictionary, parentDictionary, selectedDicId, selectedParentDicId, isSearchDone]);

  // console.log(tableData);

  // Данные версий
  useEffect(() => {
    if (terminVersions) {
      setDataVersion(
        terminVersions.map((item, index) => ({
          ...versionsTableUtils.getCommonChildTableProps(item, index, terminVersions.length),
          key: String(`${index}prop`),
          id: Number(item.id),
          fromDate: item.fromDate ? toHttpFormatStartOfDay(item.fromDate) : "",
          toDate: item.toDate ? toHttpFormatEndOfDay(item.toDate) : "",
          dicTypeAlias: selectedDicTypeAlias,
          parentDicName:
            versionsTableUtils.getDictionaryItemById(parentDictionary, item.parentDicId)?.name ||
            "",
          name: item.name,
          abbrev: item.abbrev,
          parentDicId: item.parentDicId,
          dsc: item.dsc,
          alias: item.alias,
          currentDate: moment().isBetween(item.fromDate, item.toDate),
          pastDate: moment(item.fromDate).isBefore(nowDate),
          futureDate: moment(item.fromDate).isAfter(nowDate),
          fromDateForRequestBody: item.fromDate,
          toDateForRequestBody: item.toDate,
          lastVersion: String(index) === String(0) ? "lastVersion" : "",
        }))
      );
    }
  }, [terminVersions]);

  // console.log(dataVersion);

  // const handleShowModal = (record: TableRecord) => {
  //   setActiveModal("history");
  //   setObjectDateFrom(record.fromDateForRequestBody);
  //   setObjectDateTo(record.toDateForRequestBody);
  //   setObjectId(record.id);
  // };

  // const handleHideModal = () => {
  //   setActiveModal("");
  //   setObjectId(null);
  // };

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    id: "5%",
    name: "17%",
    sortName: "35px",
    abbrev: "10%",
    parentDicId: "17%",
    alias: "10%",
    dsc: "15%",
    perion: "",
    actions: "50px",
  };

  const getColumnsInner = (): TableColumnType[] => [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      width: columnsWidths.name,
      render: (val, record) => {
        const editable = isEditing(record);

        const onChange = (e) => {
          setDicRequestBody({
            ...dicRequestBody,
            name: e.target.value,
          });
        };

        return editable && (isMakingNewDicVersion || isEditingDic) ? (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            initialValue={val}
            name="name"
            style={{ width: "103%" }}
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input onChange={onChange} />
          </Form.Item>
        ) : (
          val
        );
      },
    },
    // Сделан отдельный столбец под сортировку по названию, чтобы убрать тултип с селекта фильтра
    {
      dataIndex: "name",
      key: "sortName",
      width: columnsWidths.sortName,
      render: () => null,
    },
    {
      title: "Аббревиатура",
      dataIndex: "abbrev",
      key: "abbrev",
      width: columnsWidths.abbrev,
      render: (val, record) => {
        const editable = isEditing(record);

        const onChange = (e) => {
          setDicRequestBody({
            ...dicRequestBody,
            abbrev: e.target.value,
          });
        };

        return editable && (isMakingNewDicVersion || isEditingDic) ? (
          <Input onChange={onChange} value={dicRequestBody.abbrev} defaultValue={val} />
        ) : (
          val
        );
      },
    },
    {
      title: "Группа",
      dataIndex: "parentDicName",
      key: "parentDicName",
      width: columnsWidths.parentDicId,
      render: (val, record) => {
        const editable = isEditing(record);

        const onChange = (value) => {
          setDicRequestBody({
            ...dicRequestBody,
            parentDicId: value,
          });
        };

        return editable && (isMakingNewDicVersion || isEditingDic) ? (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            name="parentDicId"
            rules={[
              {
                required: Boolean(parentDicTypeAlias),
              },
            ]}
          >
            <Select
              allowClear
              showSearch
              filterOption={selectFilterOption}
              onChange={onChange}
              options={getSelectOptions({
                list: parentDictionary,
              })}
              disabled={!parentDicTypeAlias}
            />
          </Form.Item>
        ) : (
          val
        );
      },
    },
    {
      title: "Alias",
      dataIndex: "alias",
      key: "alias",
      width: columnsWidths.alias,
      render: (val, record) => {
        const editable = isEditing(record);

        const onChange = (e) => {
          setDicRequestBody({
            ...dicRequestBody,
            alias: e.target.value,
          });
        };

        return editable && (isMakingNewDicVersion || isEditingDic) ? (
          <Input onChange={onChange} value={dicRequestBody.alias} defaultValue={val} />
        ) : (
          val
        );
      },
    },
    {
      title: "Комментарий",
      dataIndex: "dsc",
      key: "dsc",
      width: columnsWidths.dsc,
      render: (val, record) => {
        const editable = isEditing(record);

        const onChange = (e) => {
          setDicRequestBody({
            ...dicRequestBody,
            dsc: e.target.value,
          });
        };

        return editable && (isMakingNewDicVersion || isEditingDic) ? (
          <TextArea onChange={onChange} value={dicRequestBody.dsc} defaultValue={val} />
        ) : (
          val
        );
      },
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      render: (val, record) => {
        const editable = isEditing(record);
        const fromDateValue = moment(record.fromDateForRequestBody).add(1, "day");
        const toDateValue = moment(record.toDateForRequestBody);

        const compareDates = () =>
          toDisplayFormat(record.fromDateForRequestBody) ===
          toDisplayFormat(record.toDateForRequestBody);

        const onChangeFromDate = (e) => {
          setDicRequestBody({
            ...dicRequestBody,
            fromDate: toHttpFormatStartOfDay(e),
          });
        };

        const onChangeToDate = (e) => {
          setDicRequestBody({
            ...dicRequestBody,
            toDate: toHttpFormatEndOfDay(e),
          });
        };

        const disabledDates = (cur) => {
          if (compareDates()) {
            return cur;
          }

          return cur.isBefore(fromDateValue) || cur.isAfter(toDateValue);
        };

        const defaultValues = () => {
          if (compareDates()) {
            // TODO проверить корректность
            return moment(null);
          }

          return fromDateValue;
        };

        if (editable && isEditingDicToDate) {
          return (
            <>
              {record.fromDateForRequestBody ? toDisplayFormat(record.fromDateForRequestBody) : ""}
              {" - "}
              <DatePicker
                format={displayFormat}
                onChange={onChangeToDate}
                defaultValue={toDateValue}
                disabledDate={(current) =>
                  current < fromDateValue || current > moment(infinityDateHttpFormat)
                }
                allowClear={false}
                placeholder=""
              />
            </>
          );
        }

        return editable && isMakingNewDicVersion ? (
          <>
            <DatePicker
              format={displayFormat}
              onChange={onChangeFromDate}
              defaultValue={defaultValues()}
              disabledDate={disabledDates}
              allowClear={false}
            />
            - {record.toDateForRequestBody ? toDisplayFormat(record.toDateForRequestBody) : ""}
          </>
        ) : (
          <>
            {" "}
            {record.fromDateForRequestBody
              ? toDisplayFormat(record.fromDateForRequestBody)
              : ""} -{" "}
            {record.toDateForRequestBody ? toDisplayFormat(record.toDateForRequestBody) : ""}
          </>
        );
      },
    },
    {
      title: "",
      dataIndex: "",
      key: "x",
      width: columnsWidths.actions,
      render: (val, record, index) => {
        const editable = isEditing(record);
        const disabled = isMakingNewDicVersion || isEditingDicToDate || isEditingDic;

        const nameValue = val.name;
        const fromDateValue = val.fromDateForRequestBody;
        const abbrevValue = val.abbrev;
        const dscValue = val.dsc;
        const aliasValue = val.alias;

        const handleEdit = (currencyEditStatus) => () => {
          setDicRequestBody({
            ...dicRequestBody,
            name: nameValue,
            fromDate: fromDateValue,
            abbrev: abbrevValue,
            dsc: dscValue,
            alias: aliasValue,
            toDate: toHttpFormatEndOfDay(record.toDateForRequestBody),
            parentDicId: val.parentDicId,
          });

          // Проставление значения parentDicId, т.к. не всегда подхватывается значение в форму
          form.setFieldsValue({ parentDicId: val.parentDicId });

          setEditingKey(() => record.key);
          setDicEditStatus(currencyEditStatus);
        };

        const addNewVersion = (currencyEditStatus) => () => {
          const fromDateParse = () => {
            return toHttpFormatStartOfDay(moment(record.fromDate).add(1, "day"));
          };

          setDicRequestBody({
            ...dicRequestBody,
            name: nameValue,
            dicTypeAlias: val.dicTypeAlias,
            abbrev: abbrevValue,
            dsc: dscValue,
            alias: aliasValue,
            fromDate:
              toDisplayFormat(record.fromDateForRequestBody) ===
              toDisplayFormat(record.toDateForRequestBody)
                ? ""
                : fromDateParse(),
            parentDicId: val.parentDicId,
          });

          // Проставление значения parentDicId, т.к. не всегда подхватывается значение в форму
          form.setFieldsValue({ parentDicId: val.parentDicId });

          setEditingKey(() => `new${record.key}`);
          setDicEditStatus(currencyEditStatus);

          const newRecord = {
            ...dataVersion[index],
            key: `new${dataVersion[index].key}`,
          };

          setDataVersion((prev) => [...prev.slice(0, index), newRecord, ...prev.slice(index)]);
        };

        const handleCancel = () => {
          form.resetFields();
          setEditingKey(undefined);
          if (isMakingNewDicVersion)
            setDataVersion((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);

          setDicEditStatus("idle");

          setDicRequestBody(defalultDicRequestBody);
        };

        const handleSubmit = () => {
          if (!selectedDicTypeAlias || !terminVersions) return;

          form.validateFields().then(async () => {
            const dataCreate = {
              ...dicRequestBody,
              id: record.id,
              fromDate: dicRequestBody.fromDate,
              toDate: record.toDateForRequestBody,
              name: dicRequestBody.name,
              dicTypeAlias: selectedDicTypeAlias,
              abbrev: dicRequestBody.abbrev,
              parentDicId: dicRequestBody.parentDicId,
              dsc: dicRequestBody.dsc,
              alias: dicRequestBody.alias,
            };

            if (isEditingDicToDate)
              await dispatch(
                deleteTerminVersion({
                  dicTypeAlias: selectedDicTypeAlias,
                  id: record.id,
                  fromDate: dicRequestBody.toDate,
                })
              );
            if (isMakingNewDicVersion)
              await dispatch(
                updateTerminVersion({
                  fromDate: dicRequestBody.fromDate,
                  values: dataCreate,
                })
              );

            await dispatch(
              fetchDictionary({
                dicTypeAlias: selectedDicTypeAlias,
                includeClosed: dictionariesSearchParams.includeClosed,
                onDate: dictionariesSearchParams.fromDate,
              })
            );
            await dispatch(
              fetchTerminVersions({
                dicTypeAlias: selectedDicTypeAlias,
                id: terminVersions[0].id,
              })
            );

            setEditingKey(undefined);
            setDicEditStatus("idle");
            form.resetFields();
            setDicRequestBody(defalultDicRequestBody);
          });
        };

        return editable ? (
          <>
            <Tooltip title="Подтвердить">
              <Button
                icon={<img src={tickIcon} alt="tick" width="16px" />}
                className="icon"
                type="text"
                htmlType="button"
                onClick={handleSubmit}
              />
            </Tooltip>
            <Tooltip title="Отменить">
              <Button
                icon={<img src={deleteIcon} width="16px" alt="delete" />}
                type="text"
                className="icon"
                htmlType="button"
                onClick={handleCancel}
              />
            </Tooltip>
          </>
        ) : (
          <>
            {hasAccess("MENU.ADMIN.DIC.WRITE") && (
              <>
                <Tooltip title="Добавить версию">
                  {(record.futureDate || record.currentDate) && (
                    <Button
                      icon={<img src={pencilWithPlusIcon} alt="pencil with plus" width="16px" />}
                      type="text"
                      htmlType="button"
                      onClick={addNewVersion("createNewVersion")}
                      disabled={disabled}
                    />
                  )}
                </Tooltip>
                <Tooltip title="Изменить дату закрытия">
                  {record.lastVersion && (
                    <Button
                      icon={
                        <img src={pencilWithCalendarIcon} alt="pencil with calendar" width="18px" />
                      }
                      type="text"
                      htmlType="button"
                      disabled={disabled}
                      onClick={handleEdit("editingDicToDate")}
                    />
                  )}
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
    {
      dataIndex: "",
      key: "button",
      width: columnsWidths.buttons,
      render: () => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            // handleShowModal(record);
          }}
          className="button-icon iconButton"
          style={{ outline: "none" }}
          icon={<DotsIcon />}
          disabled
        />
      ),
    },
  ];

  const [columnsInner, setColumnsInner] = useState(getColumnsInner());

  // Обновление колонок (для отображения фильтров в заголовках) с сохранением состояния hidden
  useEffect(() => {
    setColumnsInner((prev) => {
      const newColumnsInner = getColumnsInner();
      const getNewColumn = ({ key }: TableColumnType) =>
        newColumnsInner.find((item) => item.key === key);
      return prev.map((prevColumn) => {
        const newColumn = getNewColumn(prevColumn);
        if (newColumn) {
          return {
            ...prevColumn,
            title: newColumn.title,
            render: newColumn.render,
          };
        }
        return prevColumn;
      });
    });
  }, [dicRequestBody, dataVersion]);

  const handleFetchDictionary = (params: DictionariesSearchParams) => {
    if (selectedDicTypeAlias) {
      setIsSearchDone(false);
      dispatch(
        fetchDictionary({
          dicTypeAlias: selectedDicTypeAlias,
          includeClosed: params.includeClosed,
          onDate: params.fromDate,
        })
      ).then(() => {
        setIsSearchDone(true);
      });
    }
  };

  const handleDictionariesPagingAndFilters = (field, value) => {
    switch (field) {
      case "dicTypeAlias":
        if (value) {
          dispatch(fetchMaxTeminId({ dicTypeAlias: value })); // дополнительный метод для получения максм. id, необходимо передавать при создании термина
        }
        setIsSearchDone(false);
        resetTableFilters();
        setTableData([]);
        setDataVersion([]);
        setDictionariesSearchParams({
          ...dictionariesSearchParams,
          includeClosed: false,
        });
        setSelectedDicTypeAlias(value);
        break;
      case "includeClosed":
        resetTableFilters();
        setDictionariesSearchParams({
          ...dictionariesSearchParams,
          includeClosed: value,
        });
        break;
      case "fromDate":
        setDictionariesSearchParams({
          ...dictionariesSearchParams,
          fromDate: value ? toHttpFormatStartOfDay(value) : "",
        });
        break;
      default:
        break;
    }
  };

  const handleSearchDictionaries = () => {
    if (selectedDicTypeAlias) {
      handleFetchDictionary(dictionariesSearchParams);
    }
  };

  const renderDictionaryFiltersAndHeader = () => (
    <Col className="inner-row">
      <Row align="middle">
        <h1>Справочники</h1>
      </Row>
      <Row className="dictionary-filters__row" align="middle">
        <Col span={12}>
          <span className="dictionary-filters__label">Наименование</span>
          <Select
            className="dictionary-filters__select"
            showSearch
            filterOption={selectFilterOption}
            placeholder="Выбрать"
            onChange={(e) => handleDictionariesPagingAndFilters("dicTypeAlias", e)}
            allowClear
            defaultValue={selectedDicTypeAlias || undefined}
            options={getSelectOptions({
              list: dictionaryTypes,
              valueName: "alias",
            })}
          />
          <Button
            onClick={handleSearchDictionaries}
            className="dictionary-filters__submit"
            type="primary"
            disabled={!selectedDicTypeAlias}
          >
            Применить
          </Button>
        </Col>
      </Row>
      <Row className="dictionary-information">
        <Col>
          <span className="dictionary-filters__label font-roboto">Справочник:</span>
          <span className="font-roboto font-size-16 ">
            {dictionaryTypes &&
              dictionaryTypes.find(({ alias }) => alias === selectedDicTypeAlias)?.name}
          </span>
          <span>
            {hasAccess("MENU.ADMIN.DIC.WRITE") && isSearchDone && (
              <Tooltip title="Добавить термин">
                <PlusInRound
                  className="dictionary-filters__add-termin"
                  onClick={() => {
                    setActiveModal("createTermin");
                  }}
                />
              </Tooltip>
            )}
          </span>
        </Col>
      </Row>
    </Col>
  );

  const buildRowClassName = (record) => {
    const editable = isEditing(record);
    const editNew = record.key.includes("new");
    const activeClassName = "currency-version-row";

    if (record.currentDate && !editable) return `${activeClassName} current-date`;
    if (record.currentDate && editable && !editNew)
      return `${activeClassName} current-date active-row`;
    if (record.currentDate && editable && editNew)
      return `${activeClassName} current-date active-row new-prop`;
    if (record.pastDate && !editable) return `${activeClassName} past-date`;
    if (record.pastDate && editable) return `${activeClassName} past-date active-row`;
    if (record.futureDate && !editable) return `${activeClassName} future-date`;
    if (record.futureDate && editable) return `${activeClassName} future-date active-row`;
    return "";
  };

  const renderNameTitle = () => {
    const activeDicIds = parentDictionary
      ?.filter(({ id }) => (selectedParentDicId ? selectedParentDicId === id : true))
      .map(({ id }) => id);
    const list = activeDictionary?.filter(({ parentDicId }) => {
      if (!parentDicTypeAlias) return true;
      return parentDicId ? activeDicIds?.includes(parentDicId) : false;
    });

    return (
      <Select
        onChange={setSelectedDicId}
        onClick={(e) => {
          e.stopPropagation();
        }}
        placeholder="Наименование"
        style={{ width: "15vw" }}
        allowClear
        showSearch
        filterOption={selectFilterOption}
        disabled={!isSearchDone}
        options={getSelectOptions({
          list,
        })}
        value={selectedDicId}
      />
    );
  };

  renderNameTitle.toString = () => "Наименование";

  const renderParentDicNameTitle = () => {
    const disabled = !isSearchDone || !parentDicTypeAlias;
    const activeParentDicIds = activeDictionary
      ?.filter(({ id }) => (selectedDicId ? selectedDicId === id : true))
      .map(({ parentDicId }) => parentDicId);
    const list = parentDictionary?.filter(({ id }) => activeParentDicIds?.includes(id));
    return (
      <Select
        onChange={setSelectedParentDicId}
        onClick={(e) => {
          e.stopPropagation();
        }}
        placeholder="Группа"
        style={{ width: "15vw" }}
        allowClear
        showSearch
        filterOption={selectFilterOption}
        disabled={disabled}
        options={getSelectOptions({
          list,
        })}
        value={selectedParentDicId}
      />
    );
  };

  renderParentDicNameTitle.toString = () => "Группа";

  const getColumns = () => [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
      sorter: (a, b) => (a > b ? -1 : 1),
    },
    {
      title: renderNameTitle,
      dataIndex: "name",
      key: "name",
      width: columnsWidths.name,
    },
    // Сделан отдельный столбец под сортировку по названию, чтобы убрать тултип с селекта фильтра
    {
      dataIndex: "name",
      key: "sortName",
      className: "dictionary-sorter",
      width: columnsWidths.sortName,
      render: () => null,
      sorter: (a, b) => (a.name > b.name ? 1 : -1),
    },
    {
      title: "Аббревиатура",
      dataIndex: "abbrev",
      key: "abbrev",
      width: columnsWidths.abbrev,
    },
    {
      title: renderParentDicNameTitle,
      dataIndex: "parentDicName",
      key: "parentDicName",
      width: columnsWidths.parentDicId,
    },
    {
      title: "Alias",
      dataIndex: "alias",
      key: "alias",
      width: columnsWidths.alias,
    },
    {
      title: "Комментарий",
      dataIndex: "dsc",
      key: "dsc",
      width: columnsWidths.dsc,
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      render: (record) =>
        `${record.fromDate ? toDisplayFormat(record.fromDate) : ""} - ${
          record.toDate ? toDisplayFormat(record.toDate) : ""
        }`,
    },
    {
      dataIndex: "",
      key: "x",
      width: columnsWidths.buttons,
      render: () => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            // handleShowModal(record);
          }}
          className="button-icon iconButton"
          style={{ outline: "none" }}
          icon={<DotsIcon />}
          disabled
        />
      ),
    },
  ];

  const [columns, setColumns] = useState(getColumns());

  // Обновление столбцов для отображения фильтров
  useEffect(() => {
    setColumns(getColumns());
  }, [tableData]);

  const onOpenVersionsCallback = (record) => {
    form.resetFields();
    setDataVersion([]);
    setDicEditStatus("idle");
    setEditingKey("");
    if (record.id && record.dicTypeId) {
      // dicTypeId в словарях иногда не заполнен, в этом случае берется из справочника типов
      const dicTypeAlias =
        record.dicTypeAlias ||
        dictionaryTypes.find(({ id }) => String(id) === String(record.dicTypeId))?.alias; // В справочнике типов проиходят id как строка, хотя должны быть число. Приведение по этому поводу
      if (dicTypeAlias) {
        dispatch(
          fetchTerminVersions({
            dicTypeAlias,
            id: record.id,
          })
        );
      } else {
        Modal.error({
          title: "Ошибка загрузки версий",
          content: "У словаря отсутствует alias",
        });
      }
    }
  };

  const includeClosedCheckbox = (
    <Checkbox
      onChange={(e) => handleDictionariesPagingAndFilters("includeClosed", e.target.checked)}
      disabled={!selectedDicTypeAlias}
      checked={dictionariesSearchParams.includeClosed}
      style={{ marginLeft: "10px" }}
    >
      Показать закрытые
    </Checkbox>
  );

  return (
    <div className="container_height">
      {renderDictionaryFiltersAndHeader()}
      <Form form={form}>
        <ToggleableVersionsTable
          title={includeClosedCheckbox}
          tableClassName="table_no-hover version-table dictionary-table"
          tableName="dictionaries-table"
          columns={columns}
          setColumns={setColumns}
          tableData={tableData}
          rowClassName={(record) =>
            record.isActive === false ? "dictionary__disabled-date" : "dictionary__enabled-date"
          }
          isTableLoading={checkIsLoading(activeDictionaryLoading)}
          childTableClassName="inner-table"
          childTableColumns={columnsInner}
          setChildTableColumns={setColumnsInner}
          childTableData={dataVersion}
          isChildTableLoading={checkIsLoading(terminVersionsLoading)}
          onOpenVersionsCallback={onOpenVersionsCallback}
          isPageable={false}
          childRowClassName={buildRowClassName}
        />
      </Form>
      {selectedDicTypeAlias && (
        <TerminCreateModal
          open={activeModal === "createTermin"}
          dicTypeAlias={selectedDicTypeAlias}
          dictionariesSearchParams={dictionariesSearchParams}
          closeModal={() => {
            setActiveModal("");
          }}
          parentDicTypeAlias={parentDicTypeAlias}
          selectedParentDicId={selectedParentDicId}
        />
      )}
    </div>
  );
};
